import { NotificationType } from './notificationTypes';
/**
 * Which live notifications a client applies. Its data was read at `dataReadAtUtc` (the login's
 * `serverTimeUtc`): notifications written before that are already in it, and applying them could put older
 * state back. The rest are applied oldest first, so a later change always lands last — including the backlog
 * of a tab that stopped listening while hidden.
 */
export function notificationsToApply(notifications, dataReadAtUtc) {
    return notifications
        .filter((notification) => notification.createdAt >= dataReadAtUtc)
        .sort((a, b) => a.createdAt - b.createdAt);
}
/**
 * What a notification carries a full snapshot of: a later one about it replaces an earlier one. Null for
 * notifications that are events, every one of which counts.
 */
export function notificationEntityKey(notification) {
    switch (notification.type) {
        case NotificationType.CANDIDATE_UPDATED:
            return `candidate:${notification.payload.candidateId}:${notification.payload.jobId ?? ''}`;
        case NotificationType.CHAT_MESSAGE:
            return `chat:${notification.payload.chatId}:${notification.payload.message.id}`;
        case NotificationType.JOB_UPDATED:
            return `job:${notification.payload.job.id}`;
        case NotificationType.SCHEDULED_MESSAGE_UPDATED:
            return `scheduled:${notification.payload.message.id}`;
        case NotificationType.ORG_UPDATED:
            return `org:${notification.payload.orgId}`;
        case NotificationType.USER_UPDATED:
            return `user:${notification.payload.userId}`;
        default:
            return null;
    }
}
/**
 * The snapshots a tab applies: one older than a snapshot of the same thing it already applied is skipped. Servers
 * write a request's notifications together, so an older one can arrive after a newer one, and would put older
 * state back (a credit count, a message's delivery).
 */
export class NewestSnapshots {
    constructor() {
        this.newest = new Map();
    }
    /** Whether to apply this notification; if so, it's the newest of its thing from now on. */
    accept(notification) {
        const key = notificationEntityKey(notification);
        if (key === null)
            return true;
        const newest = this.newest.get(key);
        if (newest !== undefined && notification.createdAt < newest)
            return false;
        this.newest.set(key, notification.createdAt);
        return true;
    }
}
